import { useMemo } from 'react';
import { Flex } from 'native-base';
import { chain, filter, first, isEmpty, map, range, round, sumBy } from 'lodash';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

import { CleaningInsightDto } from '@pimm/services/lib/sms-workforce';
import { useAppLocale } from '@pimm/common';
import { formatToISOString } from '@app/utils/date-formatter';
import { ProgressCircleCard } from './progress-circle-card';
import { ProgressBarCard } from './progress-bar-card';

type CleaningTasksProps = {
  isWeekly?: boolean;
  startOfWeek: Date;
  cleaningInsights?: CleaningInsightDto[];
};

export const CleaningTasks = ({ isWeekly, cleaningInsights, startOfWeek }: CleaningTasksProps) => {
  const { translate } = useAppLocale();
  const { t } = useTranslation();

  const memoCleaningInsights = useMemo(() => {
    const taskStart = formatToISOString(startOfWeek, true);
    const sortOrder = { Daily: 0, Weekly: 1, Monthly: 2 };

    return chain(cleaningInsights)
      .groupBy(isWeekly ? 'scheduleType' : 'category')
      .map((tasks, key) => {
        const firstTask = first(tasks);
        return {
          scheduleType: firstTask?.scheduleType,
          category: isWeekly ? firstTask?.category : key,
          categoryTranslation: firstTask?.categoryTranslation,
          tasks: isWeekly ? tasks : tasks.filter(task => task.date?.startsWith(taskStart)),
        };
      })
      .orderBy(item => sortOrder[item.scheduleType ?? ''] ?? 99)
      .filter(item => !isEmpty(item.tasks))
      .value();
  }, [cleaningInsights, isWeekly, startOfWeek]);

  return (
    <Flex flexDirection="row" flexWrap="wrap">
      {memoCleaningInsights.map((_cleaning, index) => {
        const accessorKey = `dashboard-utilization.cleaning.${isWeekly ? 'weekly' : 'daily'}.${index}`;
        const isEven = index % 2 == 0;
        const category = _cleaning?.category?.toLowerCase();
        const translatedCategory = translate(category, _cleaning.categoryTranslation);
        const categoryTitle = isWeekly
          ? t(`app_insights:${_cleaning.scheduleType?.toLowerCase()}`)
          : _cleaning.categoryTranslation
          ? `${category?.includes('daypart') ? t('app_insights:daily') + ' - ' : ''}${translatedCategory}`
          : t(`app_insights:${category}`);

        if (isWeekly && index < 1) {
          const weeklySummary = map(range(7), day => {
            const currentDate = moment(startOfWeek).add(day, 'days');

            const tasks = filter(_cleaning?.tasks, _task => _task.date?.startsWith(currentDate.format('YYYY-MM-DD')));

            return {
              day: currentDate.format('ddd'),
              completion:
                sumBy(tasks, 'totalTaskCount') > 0 ? (sumBy(tasks, 'completedTasksCount') / sumBy(tasks, 'totalTaskCount')) * 100 : 0,
            };
          });

          return (
            <ProgressBarCard key={accessorKey} _container={{ pb: 3, w: 'full' }} title={categoryTitle} weeklySummary={weeklySummary} />
          );
        }

        return (
          <ProgressCircleCard
            key={accessorKey}
            _container={{ pb: 3, pr: isEven || isWeekly ? 1.5 : 0, pl: !isEven || isWeekly ? 1.5 : 0, w: '1/2' }}
            title={categoryTitle}
            total={round(
              sumBy(_cleaning.tasks, task => (isWeekly ? (task.totalTaskCount ?? 0) / _cleaning.tasks?.length : task.totalTaskCount ?? 0)),
            )}
            value={round(
              sumBy(_cleaning.tasks, task =>
                isWeekly ? (task.completedTasksCount ?? 0) / _cleaning.tasks?.length : task.completedTasksCount ?? 0,
              ),
            )}
          />
        );
      })}
    </Flex>
  );
};
